<script lang="ts" setup>
import Permissions from '#layers/aicore/shared/permissions';

const { $log } = useLogging();
const { $alert } = useAlerts();
const { $t } = useLocalization();
const aiStore = useAiSettingsStore();
const { $permissions } = usePermissions();

const props = withDefaults(defineProps<{
    scope: string;
    folder?: number;
    showMoveSelected?: boolean | null;
}>(), {
    folder: null,
    showMoveSelected: null
});

const emit = defineEmits<{
    (e: 'reload'): void;
}>();

const modalRef = ref<any>(null);
const modelSchema = ref<any>(null);
const modelInputs = ref<{ [key: string]: any }>({});
const tabIndex = ref(0);
const urls = ref<string[]>([]);
const imageGeneratorCanAccess = ref<boolean>(false);

const form = ref<FormType<any>>(Form.create({
    prompt: '',
    imageStyle: '',
    medium: '',
    variants: 1,
    model: ''
}));

const formImages = ref<FormType<SaveImagesRequestModel>>(Form.create({
    urlList: [],
    prompt: ''
}));

const savedImages = ref<any[]>([]);
const currentScope = ref<string | null>(null);
const currentFolder = ref<number | null>(null);

onMounted(async (): Promise<void> =>
{
    imageGeneratorCanAccess.value = await $permissions.all([Permissions.AiCore.ImageGenerator.Access]);

    currentScope.value = props.scope;
    currentFolder.value = props.folder;

    await updateModelSchema();
});

const aiEnabled = computed(() => aiStore.aiEnabled && imageGeneratorCanAccess.value);

const modelOptions = computed<Option[]>(() => aiStore.aiModelOptions(AiModelTypes.IMAGE) || []);

const defaultModel = computed<AiModelSettings>(() => aiStore.getDefaultModelByType(AiModelTypes.IMAGE));

const modelSchemaFields = computed(() =>
{
    if (!modelSchema.value) return [];

    return Object.entries(modelSchema.value as Record<string, ModelSchemaField>)
        .filter(([key]) => key !== 'prompt')
        .map(([key, value]) => ({
            key,
            title: value.title,
            description: value.description,
            type: value.type,
            format: value.format,
            minimum: value.minimum,
            maximum: value.maximum,
            default: value.default,
            enum: value.enum,
            allOf: value.allOf,
            order: value['x-order'] || 999
        }))
        .sort((a, b) => a.order - b.order);
});

const modelParts = computed(() =>
{
    if (!form.value.model) return [null, null, null];

    const [provider, owner, name] = form.value.model.split('/');

    return [provider, owner, name];
});

const variantOptions = computed(() => [
    { value: 1, text: '[[[1]]]', title: '[[[1]]]' },
    { value: 2, text: '[[[2]]]', title: '[[[2]]]' },
    { value: 3, text: '[[[3]]]', title: '[[[3]]]' },
    { value: 4, text: '[[[4]]]', title: '[[[4]]]' }
]);

const aspectRatioOptions = computed(() =>
{
    const aspectRatioField = modelSchemaFields.value.find(field => field.key === 'aspect_ratio');

    if (!aspectRatioField) return [];

    let options: string[] = [];

    if (aspectRatioField.allOf && aspectRatioField.allOf[0].enum)
    {
        options = aspectRatioField.allOf[0].enum;
    }
    else if (aspectRatioField.enum)
    {
        options = aspectRatioField.enum;
    }

    return options.map(value => ({
        value,
        text: value,
        title: value
    }));
});

const outputFormatOptions = computed(() =>
{
    const outputFormatField = modelSchemaFields.value.find(field => field.key === 'output_format');

    if (!outputFormatField) return [];

    let options: string[] = [];

    if (outputFormatField.allOf && outputFormatField.allOf[0].enum)
    {
        options = outputFormatField.allOf[0].enum;
    }
    else if (outputFormatField.enum)
    {
        options = outputFormatField.enum;
    }

    return options.map(value => ({
        value,
        text: value.toUpperCase(),
        title: value.toUpperCase()
    }));
});

const generateImages = async (): Promise<void> =>
{
    if (!aiEnabled.value) return;

    try
    {
        form.value.wait();

        urls.value = []; // Wyczyść poprzednie wyniki

        const schemaParams = modelSchema.value
            ? Object.fromEntries(
                Object.entries(modelInputs.value)
                    .filter(([key]) => key in modelSchema.value)
            )
            : {};

        const requestBody = {
            input: {
                ...schemaParams,
                prompt: constructPromptWithModifiers()
            }
        };

        const [provider, owner, name] = modelParts.value;

        if (!provider || !owner || !name) return;

        $alert.info($t('[[[Trwa generowanie obrazów]]]'));

        const variantsCount = Math.min(form.value.variants, 4); // Generuj warianty, maksymalnie 4 obrazy

        for (let i = 0; i < variantsCount; i++)
        {
            const response = await AiCoreImageService.createPrediction(requestBody, { provider, owner, name });

            if (Array.isArray(response))
            {
                if (response.length > 0) // Jeśli odpowiedź to array, dodaj pierwszy obraz
                    urls.value.push(response[0]);
            }
            else if (typeof response === 'string') // Jeśli odpowiedź to string, dodaj bezpośrednio
            {
                urls.value.push(response);
            }

            if (i < variantsCount - 1)
                await new Promise(resolve => setTimeout(resolve, 500)); // Pauza
        }

        $alert.success($t('[[[Generowanie obrazów zakończone]]]'));
    }
    catch (ex)
    {
        handleException($log, ex, {
            400: (ex: any) => $alert.warning($t(ex.message))
        });
    }
    finally
    {
        form.value.continue();
    }
};

const updateModelSchema = async () =>
{
    if (!aiEnabled.value) return;

    try
    {
        const [provider, owner, name] = modelParts.value;

        if (!provider || !owner || !name) return;

        const response = await AiCoreImageService.fetchSchema({ provider, owner, name });

        if (response)
        {
            modelSchema.value = response;

            const schemaFields = modelSchema.value as Record<string, ModelSchemaField>;

            Object.entries(schemaFields).forEach(([fieldName, fieldDef]) =>
            {
                if (fieldName !== 'prompt')
                {
                    modelInputs.value[fieldName] = fieldDef.default;
                }
            });
        }
    }
    catch (ex: any)
    {
        handleException($log, ex, {
            400: (ex: any) => $alert.warning($t(ex.message))
        });
    }
};

const { $events } = useEvents();
const selected = ref<FileResource[]>([]);
const chooseSelected = (): void =>
{
    $events.$emit('filemanager::callback::value', selected.value);
};

const saveImages = async (): Promise<void> =>
{
    if (!aiEnabled.value) return;

    try
    {
        formImages.value.prompt = form.value.prompt;

        $alert.info($t('[[[Zapisywanie obrazów...]]]'));

        const result = await AiCoreImageService.saveImages(
            formImages.value.data(), null,
            currentScope.value ?? props.scope,
            currentFolder.value ?? props.folder
        );

        savedImages.value = result || [];
        $alert.success($t('[[[Zapisywanie obrazów zakończone.]]]'));

        selected.value.push(savedImages.value[0]);
        chooseSelected();

        // modalRef.value.hide();
    }
    catch (ex)
    {
        handleException($log, ex, {
            400: (ex: any) => $alert.warning($t(ex.message))
        });
    }
    finally
    {
        emit('reload');
        formImages.value.reset();
    }
};

const showModal = (): void =>
{
    modalRef.value.show();
};

const formImagesChangeValue = (value: string): void =>
{
    if (!form.value.$loading)
    {
        const index = formImages.value.urlList.indexOf(value);

        if (index > -1)
        {
            formImages.value.urlList.splice(index, 1);
        }
        else
        {
            formImages.value.urlList.push(value);
        }
    }
};

const constructPromptWithModifiers = (): string =>
{
    const parts = [form.value.prompt];

    if (form.value.imageStyle)
    {
        parts.push(`Style: ${form.value.imageStyle}`);
    }

    if (form.value.medium)
    {
        parts.push(`Medium: ${form.value.medium}`);
    }

    return parts.join(', ');
};

const onImagesMoved = (result: any): void =>
{
    $alert.success($t('[[[Obrazy zostały przeniesione.]]]'));
    savedImages.value = [];
    modalRef.value.hide();
    emit('reload');
};

const onPathChanged = (scope: string, destination: number | null): void =>
{
    currentScope.value = scope;
    currentFolder.value = destination;
    $alert.info($t('[[[Ścieżka zapisu została zmieniona.]]]'));
};

const closeModal = (): void =>
{
    modalRef.value.hide();
};

function setPrompt(value: string): void
{
    if (!form.value) return;

    form.value.prompt = value ?? '';
}

watch(() => form.value.model, async () =>
{
    await updateModelSchema();
});

// Ustaw domyślny lub pierwszy model
watch(modelOptions, (newOptions) =>
{
    if (defaultModel.value)
    {
        form.value.model = `${defaultModel.value.aiProviderName}/${defaultModel.value.owner}/${defaultModel.value.name}`;
    }
    else
    {
        if (newOptions.length > 0 && !form.value.model)
        {
            form.value.model = newOptions[0].value;
        }
    }
}, { immediate: true });

defineExpose({
    showModal,
    setPrompt
});
</script>

<template>
    <IdeoModal
        ref="modalRef"
        size="xl"
        :title="$t('[[[Generator obrazów AI]]]')"
        modal-class="filemanager-modal"
        backdrop-class="filemanager-backdrop"
        hide-footer
    >
        <template #default>
            <div class="row">
                <div class="col-md-4">
                    <IdeoTabs v-model="tabIndex" linear fill nav-class="mb-3">
                        <IdeoTab :title="$t('[[[Generator]]]')">
                            <div class="alert alert-light">
                                <IdeoFormGroup>
                                    <ModalsSchemaInputSection
                                        v-model:form="form"
                                        :model-options="modelOptions"
                                    />
                                </IdeoFormGroup>
                                <IdeoFormGroup>
                                    <ModalsChoice
                                        v-model="form.variants"
                                        :options="variantOptions"
                                        :label="'[[[Liczba wariantów]]]'"
                                    />
                                </IdeoFormGroup>
                                <IdeoFormGroup v-if="aspectRatioOptions.length > 0">
                                    <ModalsChoice
                                        v-model="modelInputs.aspect_ratio"
                                        :options="aspectRatioOptions"
                                        :label="'[[[Proporcje obrazu]]]'"
                                        :default-options="['1:1', '3:2', '2:3']"
                                    />
                                </IdeoFormGroup>
                                <IdeoFormGroup v-if="outputFormatOptions.length > 0">
                                    <ModalsChoice
                                        v-model="modelInputs.output_format"
                                        :options="outputFormatOptions"
                                        :label="'[[[Format obrazu]]]'"
                                        :default-options="['webp', 'jpg', 'jpeg', 'png']"
                                    />
                                </IdeoFormGroup>
                                <IdeoFormGroup nospace>
                                    <ModalsImageModifiersSection
                                        v-model:form="form"
                                    />
                                </IdeoFormGroup>
                            </div>
                        </IdeoTab>
                        <IdeoTab :title="$t('[[[Ustawienia (opcjonalne)]]]')">
                            <IdeoFormGroup>
                                <ModalsAdvancedSettingsSection
                                    v-model:model-inputs="modelInputs"
                                    :model-schema-fields="modelSchemaFields"
                                />
                            </IdeoFormGroup>
                        </IdeoTab>
                    </IdeoTabs>
                    <IdeoFormGroup :nospace="formImages.urlList.length === 0">
                        <ActionButton
                            variant="fancy"
                            icon="far fa-microchip-ai"
                            :text="$t(`[[[${form.variants > 1 ? 'Generuj obrazy' : 'Generuj obraz'}]]]`)"
                            :loading="form.$loading"
                            class="w-100"
                            @click="generateImages"
                        />
                    </IdeoFormGroup>
                    <IdeoFormGroup nospace>
                        <ActionButton
                            v-if="formImages.urlList.length > 0"
                            variant="primary"
                            submit
                            icon="fas fa-check"
                            :text="$t('[[[Zapisz wybrane]]]')"
                            :disabled="formImages.urlList.length === 0"
                            class="w-100"
                            @click.prevent="saveImages()"
                        />
                    </IdeoFormGroup>
                    <IdeoFormGroup v-if="props.showMoveSelected" nospace>
                        <ModalsMoveSelected
                            :scope="currentScope"
                            :destination="currentFolder"
                            :resources="savedImages.length ? savedImages : (formImages.urlList.length ? formImages.urlList : [])"
                            :open-selector="formImages.urlList.length > 0"
                            @moved="onImagesMoved"
                            @path-changed="onPathChanged"
                        />
                    </IdeoFormGroup>
                    <IdeoFormGroup v-if="savedImages.length > 0" nospace>
                        <ActionButton
                            variant="secondary"
                            icon="far fa-times"
                            :text="$t('[[[Zamknij]]]')"
                            class="w-100"
                            @click.prevent="closeModal"
                        />
                    </IdeoFormGroup>
                </div>
                <div class="col-md-8">
                    <ModalsGeneratedImagesSection
                        :urls="urls"
                        :form-images="formImages"
                        :form="form"
                        :variants="form.variants"
                        :is-generating="form.$loading"
                        @url-toggle="formImagesChangeValue"
                    />
                </div>
            </div>
        </template>
    </IdeoModal>
</template>
